'use client';

import { createClient } from '@/lib/supabase/client';

export interface CategoryRow {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  color: string | null;
  parent_id: string | null;
  sort_order: number;
  display_order: number;
  is_active: boolean;
  is_featured: boolean;
  image: string | null;
}

// Fallback images for categories when DB has none
const CATEGORY_IMAGES: Record<string, string> = {
  properties: 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80',
  property: 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80',
  apartments: 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80',
  apartment: 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80',
  villa: 'https://img.rocket.new/generatedImages/rocket_gen_img_18454deff-1773147669731.png',
  villas: 'https://img.rocket.new/generatedImages/rocket_gen_img_18454deff-1773147669731.png',
  plot: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d9edee8f-1772097277255.png',
  plots: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d9edee8f-1772097277255.png',
  land: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d9edee8f-1772097277255.png',
  commercial: 'https://img.rocket.new/generatedImages/rocket_gen_img_10dc0526a-1774285317450.png',
  pg: 'https://img.rocket.new/generatedImages/rocket_gen_img_17e93502a-1776150761218.png',
  hostel: 'https://img.rocket.new/generatedImages/rocket_gen_img_17e93502a-1776150761218.png',
  vehicles: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80',
  cars: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80',
  bikes: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  mobiles: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800&q=80',
  electronics: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=800&q=80',
  jobs: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
  furniture: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80',
  agriculture: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800&q=80',
  services: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80',
};

const TAG_COLORS: Record<string, string> = {
  properties: '#D93025',
  property: '#D93025',
  apartments: '#D93025',
  apartment: '#D93025',
  villa: '#F59E0B',
  villas: '#F59E0B',
  plot: '#16A34A',
  plots: '#16A34A',
  land: '#16A34A',
  commercial: '#2563EB',
  pg: '#7C3AED',
  hostel: '#7C3AED',
  vehicles: '#EA580C',
  cars: '#EA580C',
  bikes: '#EA580C',
  mobiles: '#0891B2',
  electronics: '#0891B2',
  jobs: '#059669',
  furniture: '#D97706',
  agriculture: '#65A30D',
  services: '#6366F1',
};

const TAG_LABELS: Record<string, string> = {
  properties: 'Most Popular',
  property: 'Most Popular',
  apartments: 'Most Popular',
  apartment: 'Most Popular',
  villa: 'Premium',
  villas: 'Premium',
  plot: 'Investment',
  plots: 'Investment',
  land: 'Investment',
  commercial: 'Business',
  pg: 'Affordable',
  hostel: 'Affordable',
  vehicles: 'Hot',
  cars: 'Hot',
  jobs: 'Trending',
  mobiles: 'New',
  electronics: 'New',
};

export function getCategoryImage(cat: CategoryRow): string {
  if (cat.image && cat.image.startsWith('http')) return cat.image;
  const slug = cat.slug?.toLowerCase() || '';
  const name = cat.name?.toLowerCase() || '';
  return CATEGORY_IMAGES[slug] || CATEGORY_IMAGES[name] || 'https://images.unsplash.com/photo-1700682274971-a0f29a99d8b9?w=800&q=80';
}

export function getCategoryColor(cat: CategoryRow): string {
  if (cat.color) return cat.color;
  const slug = cat.slug?.toLowerCase() || '';
  const name = cat.name?.toLowerCase() || '';
  return TAG_COLORS[slug] || TAG_COLORS[name] || '#D93025';
}

export function getCategoryTag(cat: CategoryRow): string {
  const slug = cat.slug?.toLowerCase() || '';
  const name = cat.name?.toLowerCase() || '';
  return TAG_LABELS[slug] || TAG_LABELS[name] || 'Popular';
}

export async function getMainCategories(limit = 10): Promise<CategoryRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, slug, icon, color, parent_id, sort_order, display_order, is_active, is_featured, image')
    .eq('is_active', true)
    .is('parent_id', null)
    .order('display_order', { ascending: true })
    .order('sort_order', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('getMainCategories error:', error.message);
    return [];
  }
  return (data || []) as CategoryRow[];
}

export async function getCategoryListingCounts(): Promise<Record<string, number>> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('listings')
    .select('category_id')
    .eq('listing_status', 'active')
    .eq('approval_status', 'approved');

  if (error) {
    console.error('getCategoryListingCounts error:', error.message);
    return {};
  }

  const counts: Record<string, number> = {};
  (data || []).forEach((row: any) => {
    if (row.category_id) {
      counts[row.category_id] = (counts[row.category_id] || 0) + 1;
    }
  });
  return counts;
}

export async function getSubcategories(parentId: string): Promise<CategoryRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, slug, icon, color, parent_id, sort_order, display_order, is_active, is_featured, image')
    .eq('is_active', true)
    .eq('parent_id', parentId)
    .order('display_order', { ascending: true })
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('getSubcategories error:', error.message);
    return [];
  }
  return (data || []) as CategoryRow[];
}

export async function getCategoryBySlug(slug: string): Promise<CategoryRow | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, slug, icon, color, parent_id, sort_order, display_order, is_active, is_featured, image')
    .eq('slug', slug)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('getCategoryBySlug error:', error.message);
    return null;
  }
  return data as CategoryRow | null;
}
